/**
 * Component: Carousel
 * ------------------------------------------------------------------------------
 * Standard Flickity powered Carousel with options.
 *
 * @namespace Carousel
 */

import log from 'salvo-lite/log'

import Flickity from 'flickity-imagesloaded'
import 'flickity-as-nav-for'
import 'flickity-bg-lazyload'

import cssClasses from '../helpers/cssClasses'

class Carousel {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._performanceEnhancerInit = false
        this._settings = {
            adaptiveHeight: false,
            asNavFor: '',
            autoPlay: false,
            bgLazyLoad: false,
            cellAlign: 'left',
            contain: true,
            draggable: '>1',
            freeScroll: false,
            groupCells: false,
            lazyLoad: false,
            imagesLoaded: true,
            pageDots: false,
            prevNextButtons: false,
            wrapAround: true,
            // on: {
            //     change: (index) => Frame.EventBus.emit('Carousel:onChange', index)
            // },
            ...JSON.parse(elem.getAttribute('data-carousel') || '{}')
        }

        /**
         * DOM node selectors.
         */
        // this._nodeSelectors = {
        //     cells: [...document.querySelectorAll(config.cellSelector)]
        // }

        this._carousel = {}
        this._isCarouselActive = false
        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise carousel component.
     */
    onInit () {
        // if (this._nodeSelectors.cells.length <= 1 && this._elem.getAttribute('data-load-always') !== 'true') {
        //     return
        // }

        this._carousel = new Flickity(this._elem, this._settings)
        this._isCarouselActive = true

        this._conditionalPageDots()
        this._conditionalPrevNextButtons()

        if (!this._performanceEnhancerInit) {
            this._performanceEnhancer()
        }
    }

    /**
     * Trigger reload events.
     */
    _reload () {
        this._carousel.reloadCells()
        this._conditionalPageDots()
        this._conditionalPrevNextButtons()
    }

    /**
     * Trigger resize events.
     */
    _resize () {
        this._carousel.resize()
        this._conditionalPageDots()
        this._conditionalPrevNextButtons()
    }

    /**
     * Trigger select event.
     */
    _select (index, isWrapped, isInstant) {
        this._carousel.select(index, isWrapped, isInstant)
    }

    /**
     * Destroy instance.
     */
    _destroy () {
        if (Object.keys(this._carousel).length === 0) {
            return
        }

        this._carousel.destroy()
        this._isCarouselActive = false
    }

    /**
     * Add/remove class from page dots based on if there is more than one
     */
    _conditionalPageDots () {
        if (this._settings.pageDots === false) {
            return
        }

        if (this._carousel.pageDots.dots.length > 1) {
            this._carousel.pageDots.holder.classList.add(cssClasses.active)
        } else {
            this._carousel.pageDots.holder.classList.remove(cssClasses.active)
        }
    }

    /**
     * Add/remove class from prev/next buttons based on if the items stretch wider than their containers width
     */
    _conditionalPrevNextButtons () {
        if (this._settings.prevNextButtons === false) {
            return
        }

        if (this._carousel.nextButton.isEnabled || this._carousel.prevButton.isEnabled) {
            this._carousel.prevButton.element.classList.add(cssClasses.active)
            this._carousel.nextButton.element.classList.add(cssClasses.active)
        } else {
            this._carousel.prevButton.element.classList.remove(cssClasses.active)
            this._carousel.nextButton.element.classList.remove(cssClasses.active)
        }
    }

    /**
     * Improves scrolling behavior on iOS 13.1.1+.
     * - https://github.com/metafizzy/flickity/issues/959#issuecomment-533366917
     * - https://gist.github.com/bakura10/b0647ef412eb7757fa6f0d2c74c1f145
     */
    _performanceEnhancer () {
        this._performanceEnhancerInit = true
        let touchingCarousel = false
        let touchStartCoords

        document.body.addEventListener('touchstart', (event) => {
            if (event.target.closest('.flickity-slider')) {
                touchingCarousel = true
            } else {
                touchingCarousel = false
                return
            }

            touchStartCoords = {
                x: event.touches[0].pageX,
                y: event.touches[0].pageY
            }
        })

        document.body.addEventListener('touchmove', (event) => {
            if (!(touchingCarousel && event.cancelable)) {
                return
            }

            const moveVector = {
                x: event.touches[0].pageX - touchStartCoords.x,
                y: event.touches[0].pageY - touchStartCoords.y
            }

            if (Math.abs(moveVector.x) > 7) {
                event.preventDefault()
            }
        }, { passive: false })
    }

    _isActive () {
        return this._isCarouselActive
    }

    // /**
    //  * Expose public interface.
    //  */
    // return Object.freeze({
    //   init,
    //   reload,
    //   resize,
    //   select,
    //   destroy,
    //   isActive,
    // })
}

export default Carousel



// WEBPACK FOOTER //
// ./src/resources/scripts/components/Carousel.js